import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action, records } from '../../lib/api.ts';
import { usePlaces } from '../../lib/places.tsx';
import type { FloorPlan } from '../../lib/types.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useRecords, useSave } from './common.tsx';

interface Floor {
  id: string;
  name: string;
  sequence: number;
  location: string | null;
}
interface Table {
  id: string;
  floor: string;
  name: string;
  seats: number;
  shape: 'square' | 'round' | 'rect';
  x: number;
  y: number;
  w: number;
  h: number;
  active: boolean;
}

const COLS = 12;
type Box = Pick<Table, 'x' | 'y' | 'w' | 'h'>;
type Edge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
const EDGES: Edge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

/** The table's new box when the given edge is pulled to the cell under the pointer. It never shrinks below one cell. */
function pull(start: Box, edge: Edge, cx: number, cy: number): Box {
  let { x, y, w, h } = start;
  if (edge.includes('e')) w = Math.min(COLS - x, Math.max(1, cx - x + 1));
  if (edge.includes('s')) h = Math.max(1, cy - y + 1);
  if (edge.includes('w')) {
    const right = x + w;
    x = Math.max(0, Math.min(cx, right - 1));
    w = right - x;
  }
  if (edge.includes('n')) {
    const bottom = y + h;
    y = Math.max(0, Math.min(cy, bottom - 1));
    h = bottom - y;
  }
  return { x, y, w, h };
}
const overlaps = (a: Pick<Table, 'x' | 'y' | 'w' | 'h'>, b: Pick<Table, 'x' | 'y' | 'w' | 'h'>) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** First free spot, scanning row by row, so a new table never lands on another one. */
function freeSpot(tables: Table[], w: number, h: number) {
  for (let y = 0; y < 200; y++) {
    for (let x = 0; x + w <= COLS; x++) {
      if (!tables.some((t) => overlaps({ x, y, w, h }, t))) return { x, y };
    }
  }
  return { x: 0, y: 0 };
}

export function Tables() {
  const { fail, toast } = useUi();
  const { rows: allFloors, reload: reloadFloors } = useRecords<Floor>('restaurant.floor', { order_by: 'sequence' });
  // Each location has its own floors; the manager works on the one chosen at the top.
  const { here, multi, at } = usePlaces();
  const floors = useMemo(() => allFloors?.filter((f) => !multi || at(f.location, here?.id)) ?? null, [allFloors, multi, at, here]);
  const { rows: all, reload } = useRecords<Table>('restaurant.table', { order_by: 'name', limit: '2000' });
  const [floorId, setFloorId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [plan, setPlan] = useState<FloorPlan | null>(null);
  const [floorDialog, setFloorDialog] = useState<Floor | 'new' | null>(null);
  const [removing, setRemoving] = useState<Table | null>(null);
  const [local, setLocal] = useState<Table[] | null>(null);

  useEffect(() => {
    action<FloorPlan>('restaurant.floor_plan', multi && here ? { location: here.id } : {}).then(setPlan, () => setPlan(null));
  }, [all, multi, here]);
  useEffect(() => setLocal(all), [all]);

  const floor = (floorId && floors?.some((f) => f.id === floorId) ? floorId : null) ?? floors?.[0]?.id ?? null;
  const tables = useMemo(() => (local ?? []).filter((x) => x.floor === floor && x.active), [local, floor]);
  const hidden = useMemo(() => (local ?? []).filter((x) => x.floor === floor && !x.active), [local, floor]);
  const current = tables.find((x) => x.id === selected) ?? null;
  const hasOpenOrder = (id: string) => !!plan?.tables.find((x) => x.id === id)?.order;

  const move = async (tbl: Table, x: number, y: number) => {
    const next = { ...tbl, x, y };
    if (tables.some((o) => o.id !== tbl.id && overlaps(next, o))) {
      toast(t('tables.overlap'), 'danger');
      return;
    }
    setLocal((ts) => (ts ?? []).map((o) => (o.id === tbl.id ? next : o)));
    try {
      await records.update('restaurant.table', tbl.id, { x, y });
    } catch (err) {
      fail(err);
      void reload();
    }
  };

  const resize = async (tbl: Table, box: Box) => {
    if (tables.some((o) => o.id !== tbl.id && overlaps(box, o))) {
      toast(t('tables.overlap'), 'danger');
      return;
    }
    // A square table pulled out of shape becomes a rectangle; a round one becomes an oval.
    const shape = tbl.shape === 'square' && box.w !== box.h ? 'rect' : tbl.shape;
    const next = { ...tbl, ...box, shape };
    setLocal((ts) => (ts ?? []).map((o) => (o.id === tbl.id ? next : o)));
    try {
      await records.update('restaurant.table', tbl.id, { ...box, shape });
    } catch (err) {
      fail(err);
      void reload();
    }
  };

  const addTable = async () => {
    if (!floor) return;
    const used = new Set((local ?? []).map((x) => x.name));
    const numbers = (local ?? []).map((x) => Number.parseInt(x.name, 10)).filter(Number.isFinite);
    let n = numbers.length ? Math.max(...numbers) + 1 : 1;
    while (used.has(String(n))) n++;
    const spot = freeSpot(tables, 2, 2);
    try {
      const created = await records.create<Table>('restaurant.table', { floor, name: String(n), seats: 4, shape: 'square', w: 2, h: 2, ...spot });
      await reload();
      setSelected(created.id);
      toast(t('tables.added', { name: created.name }));
    } catch (err) {
      fail(err);
    }
  };

  const restore = async (tbl: Table) => {
    const spot = tables.some((o) => overlaps(tbl, o)) ? freeSpot(tables, tbl.w, tbl.h) : { x: tbl.x, y: tbl.y };
    try {
      await records.update('restaurant.table', tbl.id, { active: true, ...spot });
      await reload();
      setSelected(tbl.id);
    } catch (err) {
      fail(err);
    }
  };

  const floorTables = (local ?? []).filter((x) => x.floor === floor);

  return (
    <ManageLayout
      title={t('manage.tables')}
      subtitle={multi && here ? t('tables.subtitle_at', { name: here.name }) : t('tables.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => void addTable()} disabled={!floor}>
          {t('tables.add')}
        </Button>
      }
    >
      <div className="toolbar">
        <div className="segmented" role="tablist" aria-label={t('floor.floors')}>
          {(floors ?? []).map((f) => (
            <button
              key={f.id}
              role="tab"
              aria-selected={f.id === floor}
              className={f.id === floor ? 'active' : ''}
              onClick={() => {
                setFloorId(f.id);
                setSelected(null);
              }}
            >
              {f.name}
            </button>
          ))}
        </div>
        <div className="page-actions">
          {floor ? (
            <Button icon="edit" onClick={() => setFloorDialog(floors!.find((f) => f.id === floor)!)}>
              {t('tables.rename_floor')}
            </Button>
          ) : null}
          <Button icon="plus" onClick={() => setFloorDialog('new')}>
            {t('tables.add_floor')}
          </Button>
        </div>
      </div>

      {!floors || !local ? (
        <Skeleton h={420} r="var(--radius-lg)" />
      ) : floors.length === 0 ? (
        <EmptyState icon="table" title={t('tables.no_floors')} body={t('tables.no_floors_body')} />
      ) : (
        <div className="plan-editor">
          <PlanCanvas tables={tables} selected={selected} onSelect={setSelected} onMove={move} onResize={resize} />
          <aside className="plan-side">
            {current ? (
              <TableForm
                key={`${current.id}:${current.w}x${current.h}:${current.shape}`}
                table={current}
                others={floorTables.filter((x) => x.id !== current.id)}
                onSaved={(tbl) => setLocal((ts) => (ts ?? []).map((o) => (o.id === tbl.id ? tbl : o)))}
                onRemove={() => (hasOpenOrder(current.id) ? toast(t('tables.has_order', { name: current.name }), 'danger') : setRemoving(current))}
              />
            ) : (
              <div className="plan-help">
                <Icon name="move" size={28} />
                <p>{t('tables.help')}</p>
                <p className="muted">{tn('tables.count', tables.length)}</p>
              </div>
            )}
            {hidden.length ? (
              <div className="pick-section">
                <h3>{t('tables.removed')}</h3>
                {hidden.map((x) => (
                  <div key={x.id} className="list-row compact">
                    <span className="list-title">{t('table.name', { name: x.name })}</span>
                    <Button size="sm" icon="undo" onClick={() => void restore(x)}>
                      {t('menu.restore')}
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
          </aside>
        </div>
      )}

      {floorDialog ? (
        <FloorDialog
          floor={floorDialog === 'new' ? null : floorDialog}
          tableCount={floorDialog === 'new' ? 0 : (local ?? []).filter((x) => x.floor === floorDialog.id).length}
          nextSequence={Math.max(0, ...(floors ?? []).map((f) => f.sequence)) + 10}
          place={multi ? here : null}
          onClose={() => setFloorDialog(null)}
          onSaved={(f) => {
            setFloorDialog(null);
            setFloorId(f?.id ?? null);
            void reloadFloors();
          }}
        />
      ) : null}
      {removing ? (
        <Confirm
          title={t('tables.remove_title', { name: removing.name })}
          body={t('tables.remove_body')}
          confirm={t('tables.remove')}
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await action('restaurant.remove_table', { table: removing.id });
              toast(t('tables.removed_toast', { name: removing.name }));
              setSelected(null);
              setRemoving(null);
              void reload();
            } catch (err) {
              fail(err);
            }
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

/** The floor drawn as a 12-column grid. Tables move by dragging, or with the arrow keys once selected. */
function PlanCanvas({
  tables,
  selected,
  onSelect,
  onMove,
  onResize,
}: {
  tables: Table[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  onMove: (tbl: Table, x: number, y: number) => void;
  onResize: (tbl: Table, box: Box) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number; x: number; y: number; moved: boolean } | null>(null);
  const [sizing, setSizing] = useState<{ id: string; edge: Edge; box: Box; moved: boolean } | null>(null);
  const rows = Math.max(8, ...tables.map((x) => x.y + x.h + 2), sizing ? sizing.box.y + sizing.box.h + 2 : 0);

  const cellAt = (e: PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const size = r.width / COLS;
    return { cx: Math.floor((e.clientX - r.left) / size), cy: Math.floor((e.clientY - r.top) / size) };
  };

  const down = (e: PointerEvent<HTMLButtonElement>, tbl: Table) => {
    if (e.button !== 0) return;
    const edge = (e.target as HTMLElement).dataset.edge as Edge | undefined;
    if (edge) {
      e.currentTarget.setPointerCapture(e.pointerId);
      setSizing({ id: tbl.id, edge, box: { x: tbl.x, y: tbl.y, w: tbl.w, h: tbl.h }, moved: false });
      return;
    }
    const { cx, cy } = cellAt(e);
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ id: tbl.id, dx: cx - tbl.x, dy: cy - tbl.y, x: tbl.x, y: tbl.y, moved: false });
    onSelect(tbl.id);
  };
  const moveTo = (e: PointerEvent, tbl: Table) => {
    if (sizing?.id === tbl.id) {
      const { cx, cy } = cellAt(e);
      const box = pull(tbl, sizing.edge, cx, cy);
      const same = box.x === sizing.box.x && box.y === sizing.box.y && box.w === sizing.box.w && box.h === sizing.box.h;
      if (!same) setSizing({ ...sizing, box, moved: true });
      return;
    }
    if (!drag || drag.id !== tbl.id) return;
    const { cx, cy } = cellAt(e);
    const x = Math.min(COLS - tbl.w, Math.max(0, cx - drag.dx));
    const y = Math.max(0, cy - drag.dy);
    if (x !== drag.x || y !== drag.y) setDrag({ ...drag, x, y, moved: true });
  };
  const up = (tbl: Table) => {
    if (sizing?.id === tbl.id) {
      if (sizing.moved) onResize(tbl, sizing.box);
      setSizing(null);
      return;
    }
    if (drag?.id === tbl.id && drag.moved) onMove(tbl, drag.x, drag.y);
    setDrag(null);
  };
  const key = (e: KeyboardEvent, tbl: Table) => {
    const step: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const s = step[e.key];
    if (!s) return;
    e.preventDefault();
    if (e.shiftKey) {
      // Shift + arrows changes the size: right and down grow the table, left and up shrink it.
      const w = Math.min(COLS - tbl.x, Math.max(1, tbl.w + s[0]));
      const h = Math.max(1, tbl.h + s[1]);
      if (w !== tbl.w || h !== tbl.h) onResize(tbl, { x: tbl.x, y: tbl.y, w, h });
      return;
    }
    const x = Math.min(COLS - tbl.w, Math.max(0, tbl.x + s[0]));
    const y = Math.max(0, tbl.y + s[1]);
    if (x !== tbl.x || y !== tbl.y) onMove(tbl, x, y);
  };

  return (
    <div className="plan-scroll">
      <div
        ref={ref}
        className="plan-canvas"
        style={{ ['--rows' as string]: rows, ['--cols' as string]: COLS }}
        onPointerDown={(e) => e.target === e.currentTarget && onSelect(null)}
      >
        {tables.map((tbl) => {
          const box = sizing?.id === tbl.id ? sizing.box : drag?.id === tbl.id ? { ...tbl, x: drag.x, y: drag.y } : tbl;
          const busy = drag?.id === tbl.id || sizing?.id === tbl.id;
          const clash = busy && tables.some((o) => o.id !== tbl.id && overlaps(box, o));
          const state = `${selected === tbl.id ? ' selected' : ''}${busy ? ' dragging' : ''}${sizing?.id === tbl.id ? ' sizing' : ''}${clash ? ' clash' : ''}`;
          return (
            <button
              key={tbl.id}
              className={`plan-table shape-${tbl.shape}${state}`}
              style={{
                ['--x' as string]: box.x,
                ['--y' as string]: box.y,
                ['--w' as string]: box.w,
                ['--h' as string]: box.h,
              }}
              aria-label={t('tables.table_aria', { name: tbl.name, seats: tbl.seats })}
              aria-pressed={selected === tbl.id}
              onPointerDown={(e) => down(e, tbl)}
              onPointerMove={(e) => moveTo(e, tbl)}
              onPointerUp={() => up(tbl)}
              onPointerCancel={() => {
                setDrag(null);
                setSizing(null);
              }}
              onKeyDown={(e) => key(e, tbl)}
              onFocus={() => onSelect(tbl.id)}
            >
              <strong>{tbl.name}</strong>
              <span>
                <Icon name="users" size={12} /> {tbl.seats}
              </span>
              {selected === tbl.id ? EDGES.map((edge) => <i key={edge} className={`plan-handle ${edge}`} data-edge={edge} aria-hidden="true" />) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TableForm({ table, others, onSaved, onRemove }: { table: Table; others: Table[]; onSaved: (t: Table) => void; onRemove: () => void }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(table.name);
  const [seats, setSeats] = useState(table.seats);
  const [shape, setShape] = useState(table.shape);
  const [size, setSize] = useState(`${table.w}x${table.h}`);
  const [w, h] = size.split('x').map(Number) as [number, number];
  const clash = others.some((o) => o.active && overlaps({ x: table.x, y: table.y, w, h }, o));
  const nameTaken = others.some((o) => o.name.toLowerCase() === name.trim().toLowerCase());
  const dirty = name !== table.name || seats !== table.seats || shape !== table.shape || w !== table.w || h !== table.h;

  const save = () => {
    if (!name.trim() || nameTaken || clash) return;
    let saved: Table | null = null;
    void run(async () => {
      saved = await records.update<Table>('restaurant.table', table.id, { name: name.trim(), seats, shape, w, h });
    }, t('menu.saved', { name: t('table.name', { name: name.trim() }) })).then((ok) => ok && saved && onSaved(saved));
  };

  return (
    <div className="table-form">
      <h2>{t('table.name', { name: table.name })}</h2>
      <Field label={t('tables.name')} error={nameTaken ? t('tables.name_taken') : !name.trim() ? t('form.required') : null}>
        <input className="input" value={name} maxLength={20} onChange={(e) => setName(e.target.value)} />
      </Field>
      <fieldset className="option-group">
        <legend>{t('tables.seats')}</legend>
        <div className="stepper lg">
          <IconButton icon="minus" label={t('common.less')} onClick={() => setSeats((n) => Math.max(1, n - 1))} />
          <span className="stepper-value">{seats}</span>
          <IconButton icon="plus" label={t('common.more')} onClick={() => setSeats((n) => Math.min(99, n + 1))} />
        </div>
      </fieldset>
      <fieldset className="option-group">
        <legend>{t('tables.shape')}</legend>
        <div className="segmented" role="radiogroup">
          {(['square', 'round', 'rect'] as const).map((s) => (
            <button
              key={s}
              role="radio"
              aria-checked={shape === s}
              className={shape === s ? 'active' : ''}
              onClick={() => {
                setShape(s);
                if (s === 'rect' && w === h) setSize(`${Math.min(COLS, w + 1)}x${h}`);
                if (s !== 'rect' && w !== h) setSize(`${h}x${h}`);
              }}
            >
              {t(`tables.shape_${s}`)}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="option-group">
        <legend>{t('tables.size')}</legend>
        <div className="segmented" role="radiogroup">
          {(shape === 'rect' ? (['3x2', '4x2', '6x2'] as const) : (['1x1', '2x2', '3x3'] as const)).map((s) => (
            <button key={s} role="radio" aria-checked={size === s} className={size === s ? 'active' : ''} onClick={() => setSize(s)}>
              {t(`tables.size_${s}`)}
            </button>
          ))}
        </div>
        {clash ? <p className="form-hint">{t('tables.size_clash')}</p> : null}
      </fieldset>
      <div className="table-form-actions">
        <Button variant="ghost" icon="trash" onClick={onRemove} disabled={busy}>
          {t('tables.remove')}
        </Button>
        <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!dirty || nameTaken || clash || !name.trim()}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

function FloorDialog({
  floor,
  tableCount,
  nextSequence,
  place,
  onClose,
  onSaved,
}: {
  floor: Floor | null;
  tableCount: number;
  nextSequence: number;
  /** Where a new floor goes, when the business has more than one location. */
  place: { id: string; name: string } | null;
  onClose: () => void;
  onSaved: (f: Floor | null) => void;
}) {
  const { busy, run } = useSave();
  const [name, setName] = useState(floor?.name ?? '');
  const save = () => {
    if (!name.trim()) return;
    let saved: Floor | null = null;
    void run(async () => {
      saved = floor
        ? await records.update<Floor>('restaurant.floor', floor.id, { name: name.trim() })
        : await records.create<Floor>('restaurant.floor', { name: name.trim(), sequence: nextSequence, ...(place ? { location: place.id } : {}) });
    }, floor ? t('menu.saved', { name: name.trim() }) : t('menu.added', { name: name.trim() })).then((ok) => ok && onSaved(saved));
  };
  const remove = () => void run(() => records.remove('restaurant.floor', floor!.id), t('tables.floor_deleted', { name: floor!.name })).then((ok) => ok && onSaved(null));
  return (
    <Dialog
      title={floor ? t('tables.rename_floor') : t('tables.add_floor')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {floor ? (
            <Button
              variant="ghost"
              icon="trash"
              onClick={remove}
              disabled={busy || tableCount > 0}
              title={tableCount > 0 ? t('tables.floor_not_empty') : undefined}
            >
              {t('tables.delete_floor')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('tables.floor_name')} hint={t('tables.floor_name_hint')}>
        <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} data-autofocus />
      </Field>
      {!floor && place ? (
        <p className="muted">
          <Icon name="pin" size={14} /> {t('tables.floor_at', { name: place.name })}
        </p>
      ) : null}
      {floor && tableCount > 0 ? <p className="muted">{t('tables.floor_not_empty')}</p> : null}
    </Dialog>
  );
}
